import {
  type Sections,
  type StatusReference,
  type FunnelReferenceMeta,
} from '../../../types/funnels/statuses/status-description.types';
import {
  SECTION_LABEL,
  SECTION_ORDER,
  headerToKey,
} from '../../../components/molecules/funnels/statuses/status-description.config';
import { type ReferenceDefinition } from 'src/modules/shared/utils/reference/reference-highlighter.util';

/**
 * Extracts all step numbers from the pasos section content.
 * Matches lines starting with "number." pattern.
 *
 * @param pasosContent - The pasos section content to parse
 * @returns Sorted array of unique step numbers found
 */
export const extractStepNumbers = (pasosContent: string): number[] => {
  if (!pasosContent) return [];
  const numbers = new Set<number>();
  const lines = pasosContent.replace(/\r\n/g, '\n').split('\n');
  for (const line of lines) {
    const m = line.match(/^\s*(\d+)\.\s/);
    if (m) {
      const n = parseInt(m[1] || '', 10);
      if (!Number.isNaN(n)) numbers.add(n);
    }
  }
  return Array.from(numbers).sort((a, b) => a - b);
};

/**
 * Returns CSS classes for section height based on section type.
 *
 * @param section - The section key to get height classes for
 * @returns Tailwind height classes
 */
export const getSectionHeightClasses = (section: keyof Sections): string => {
  if (section === 'pasos') {
    return 'min-h-[400px] max-h-[400px]';
  }
  if (section === 'seguimiento') {
    return 'min-h-[180px] max-h-[180px]';
  }
  return 'min-h-[120px] max-h-[120px]';
};

/**
 * Parses a composed description string into structured sections.
 * Handles legacy unassigned content by merging into entrada section.
 *
 * @param description - The full description string to parse
 * @returns Parsed sections object
 */
export const parseDescription = (description: string): Sections => {
  const initialSections: Sections = {
    entrada: '',
    objetivo: '',
    pasos: '',
    excepciones: '',
    seguimiento: '',
  };

  if (!description?.trim()) {
    return initialSections;
  }

  const lines = description.replace(/\r\n/g, '\n').split('\n');

  const buckets: Record<keyof Sections | 'unassigned', string[]> = {
    entrada: [],
    objetivo: [],
    pasos: [],
    excepciones: [],
    seguimiento: [],
    unassigned: [],
  };

  let currentSection: keyof Sections | 'unassigned' = 'unassigned';
  const headerPattern = /^\s*\[(.+?)\]\s*$/;

  lines.forEach((line) => {
    const match = line.match(headerPattern);

    if (match) {
      const nextSection = headerToKey(match[1] ?? '');
      currentSection = nextSection ?? 'unassigned';
      return;
    }

    buckets[currentSection].push(line);
  });

  const entradaLines = [...buckets.entrada];
  const unassignedContent = buckets.unassigned.join('\n').trim();

  if (unassignedContent) {
    if (entradaLines.length > 0) {
      entradaLines.push('');
    }
    entradaLines.push(...buckets.unassigned);
  }

  return {
    entrada: entradaLines.join('\n').trim(),
    objetivo: buckets.objetivo.join('\n').trim(),
    pasos: buckets.pasos.join('\n').trim(),
    excepciones: buckets.excepciones.join('\n').trim(),
    seguimiento: buckets.seguimiento.join('\n').trim(),
  };
};

/**
 * Composes structured sections into a formatted description string.
 * Skips empty sections and returns empty string if no content exists.
 *
 * @param sections - The sections object to compose
 * @returns Formatted description string
 */
export const composeDescription = (sections: Sections): string => {
  const hasAnyContent = SECTION_ORDER.some(
    (key) => sections[key].trim().length > 0
  );

  if (!hasAnyContent) {
    return '';
  }

  return SECTION_ORDER.map((key) => {
    const label = SECTION_LABEL[key];
    const content = sections[key].trim();
    if (content) {
      return `[${label}]\n${content}`;
    }
    return `[${label}]`;
  }).join('\n\n');
};

/**
 * Converts a bulleted string into rows, stripping bullet characters.
 * Supports dash, bullet, and asterisk prefixes.
 *
 * @param value - The bulleted string to parse
 * @returns Array of row strings without bullet characters
 */
export const exceptionsStringToRows = (value: string): string[] => {
  if (!value) return [];
  const lines = value.replace(/\r\n/g, '\n').split('\n');
  const rows: string[] = [];
  for (const line of lines) {
    const m = line.match(/^\s*[-•*]\s*(.*)$/);
    if (m) {
      rows.push(m[1] ?? '');
    }
  }
  return rows;
};

/**
 * Converts an array of rows into a bulleted string.
 * Each row is prefixed with "- ".
 *
 * @param rows - Array of rows to format
 * @returns Bulleted string with each row on a new line
 */
export const rowsToExceptionsString = (rows: string[]): string =>
  rows
    .map((r) => r ?? '')
    .map((r) => `- ${r}`)
    .join('\n');

/**
 * Attempts to parse a string ID into a numeric value.
 * Returns null if parsing fails or results in NaN.
 *
 * @param id - The string ID to parse
 * @returns The parsed number or null if parsing fails
 */
export const parseReferenceId = (id: string): number | null => {
  const numericId = Number.parseInt(id, 10);
  return Number.isNaN(numericId) ? null : numericId;
};

/**
 * Creates a status reference definition that resolves status IDs to their names.
 *
 * @param statusReferences - Array of available status references
 * @returns A reference definition for status resolution
 */
export const createStatusDefinition = (
  statusReferences: StatusReference[]
): ReferenceDefinition<FunnelReferenceMeta> => ({
  kind: 'status',
  resolve: (id) => {
    const numericId = parseReferenceId(id);
    if (numericId === null) return null;

    const status = statusReferences.find((ref) => ref.id === numericId);
    if (!status) return null;

    return {
      label: status.name,
      meta: { statusId: numericId },
    };
  },
});

/**
 * Creates a step reference definition that resolves step numbers to localized labels.
 *
 * @param validStepNumbers - Array of valid step numbers
 * @returns A reference definition for step resolution
 */
export const createStepDefinition = (
  validStepNumbers: number[] | undefined
): ReferenceDefinition<FunnelReferenceMeta> => ({
  kind: 'step',
  resolve: (id) => {
    const numericId = parseReferenceId(id);
    if (numericId === null) return null;

    const steps = validStepNumbers ?? [];
    if (!steps.includes(numericId)) return null;

    return {
      label: `paso ${numericId}`,
      meta: { stepNumber: numericId },
    };
  },
});
